// Case drill-down: click a grid cell, get exactly that cell's turns.

import { useEffect, useState } from "react";
import { http, qs } from "../api";
import { Badge, Dialog, DialogFooter, Empty, Toggle, useAsync } from "../ui";
import { type EvalTurn, type TurnsResponse } from "./types";
import { dash, num, fmtPct, fmtInt, fmtMs, shortId, Loadable, Tagish } from "./format";

/**
 * Click a grid cell, get exactly that cell's turns; click a turn, get the full
 * pipeline for it. The population is defined by the SAME axis=value filters the
 * cell was aggregated under, sent to the same server-side allowlist — a
 * drill-down assembled from a different query can quietly show a different set
 * of rows and the reader has no way to notice.
 */
export const LIGHTBOX_LIMIT = 400;

export function CaseLightbox({
  open,
  onClose,
  runId,
  filters,
  title,
  subtitle,
  cellN,
  cellPassed,
}: {
  open: boolean;
  onClose: () => void;
  runId: string;
  filters: Record<string, string>;
  title: string;
  subtitle?: string;
  cellN?: number;
  cellPassed?: number;
}) {
  const key = JSON.stringify(filters);
  const [selected, setSelected] = useState<EvalTurn | null>(null);
  const [failsOnly, setFailsOnly] = useState(false);

  const turns = useAsync<TurnsResponse>(
    () =>
      open && runId
        ? http.get<TurnsResponse>(
            `/chatbot/eval-ops/turns${qs({ run_id: runId, limit: LIGHTBOX_LIMIT, ...filters })}`
          )
        : Promise.resolve({ run_id: runId, turns: [], count: 0 }),
    [open, runId, key]
  );

  useEffect(() => {
    if (open) setSelected(null);
  }, [open, key]);

  const rows = turns.data?.turns ?? [];
  const failed = rows.filter((r) => !r.passed).length;
  const visible = failsOnly ? rows.filter((r) => !r.passed) : rows;
  const capped = rows.length >= LIGHTBOX_LIMIT;

  return (
    <Dialog open={open} onClose={onClose} title={title} full>
      {subtitle && (
        <p className="muted" style={{ fontSize: 12, marginBottom: "var(--sp-3)" }}>
          {subtitle}
        </p>
      )}

      <Loadable loading={turns.loading} error={turns.error} what="Failed to load the cell's turns" cols={6}>
        {selected ? (
          <div className="stack">
            <div className="row">
              <button className="btn sm" onClick={() => setSelected(null)}>
                ‹ Back to cases
              </button>
              <Badge tone={selected.passed ? "good" : "critical"}>{selected.passed ? "pass" : "fail"}</Badge>
              <span className="muted" style={{ fontSize: 12 }}>
                {dash(selected.case_id)} · {shortId(selected.conversation_id)}#{selected.turn_index} ·{" "}
                {dash(selected.turn_owner)} · {fmtMs(selected.latency_ms)} · {fmtInt(selected.total_tokens)} tok ·
                grader {dash(selected.grader)}
              </span>
            </div>

            <div className="grid c2">
              <div className="stack">
                <div>
                  <div className="muted" style={{ fontSize: 11 }}>
                    User said
                  </div>
                  <div className="prewrap" style={{ fontSize: 12 }}>
                    {dash(selected.user_text)}
                  </div>
                </div>
                <div>
                  <div className="row muted" style={{ fontSize: 11 }}>
                    Bot replied
                    {Boolean(selected.bubble_count) && (
                      <Badge>
                        {selected.bubble_count} {num(selected.bubble_count) === 1 ? "bubble" : "bubbles"}
                      </Badge>
                    )}
                  </div>
                  <div className="prewrap" style={{ fontSize: 12 }}>
                    {dash(selected.reply_text)}
                  </div>
                </div>
              </div>

              <div className="stack">
                {!selected.passed && (
                  <div>
                    <div className="muted" style={{ fontSize: 11 }}>
                      Failed checks
                    </div>
                    <div className="chips">
                      {(selected.fails ?? []).map((f) => (
                        <Badge key={f} tone="critical">
                          {f}
                        </Badge>
                      ))}
                      {!(selected.fails ?? []).length && (
                        <span className="muted" style={{ fontSize: 12 }}>
                          no labelled check — the harness failed the turn without naming a rule
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {Boolean((selected.required_actions ?? []).length) && (
                  <div>
                    <div className="muted" style={{ fontSize: 11 }}>
                      Required of this turn
                    </div>
                    <div className="chips">
                      {(selected.required_actions ?? []).map((a) => (
                        <Badge key={a} tone={(selected.delivered_actions ?? []).includes(a) ? "good" : "critical"}>
                          {a}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {Boolean((selected.forbidden_actions ?? []).length) && (
                  <div>
                    <div className="muted" style={{ fontSize: 11 }}>
                      Forbidden
                    </div>
                    <div className="chips">
                      {(selected.forbidden_actions ?? []).map((a) => (
                        <Badge key={a} tone={(selected.delivered_actions ?? []).includes(a) ? "critical" : "neutral"}>
                          {a}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {Boolean((selected.delivered_actions ?? []).length) && (
                  <div>
                    <div className="muted" style={{ fontSize: 11 }}>
                      Delivered
                    </div>
                    <div className="chips">
                      {(selected.delivered_actions ?? []).map((a) => (
                        <Tagish key={a}>{a}</Tagish>
                      ))}
                    </div>
                  </div>
                )}
                {Boolean((selected.soft ?? []).length) && (
                  <div>
                    <div className="muted" style={{ fontSize: 11 }}>
                      Soft findings (do not gate)
                    </div>
                    <div className="chips">
                      {(selected.soft ?? []).map((s) => (
                        <Badge key={s} tone="warning">
                          {s}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {selected.judge_verdict && (
                  <div>
                    <div className="strong" style={{ fontSize: 12 }}>
                      Judge: {selected.judge_verdict}
                    </div>
                    {selected.judge_reason && (
                      <div className="muted" style={{ fontSize: 12 }}>
                        {selected.judge_reason}
                      </div>
                    )}
                  </div>
                )}
                <div className="muted" style={{ fontSize: 11 }}>
                  suite {dash(selected.suite)} · lang {dash(selected.lang)} · scam {dash(selected.scam_strata)} · user{" "}
                  {dash(selected.user_strata)} · archetype {dash(selected.archetype)} · exposure{" "}
                  {dash(selected.exposure)} · affect {dash(selected.affect)} · act {dash(selected.turn_act)} ·
                  saturation {dash(selected.saturation)} · certainty {dash(selected.certainty)} · continuity{" "}
                  {dash(selected.continuity)}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="row" style={{ marginBottom: "var(--sp-2)", fontSize: 12 }}>
              <span className="muted">
                <span className="strong">{fmtInt(cellN ?? rows.length)}</span> scored ·{" "}
                <span className="strong">{fmtInt(rows.length)}</span> shown
                {cellN
                  ? ` · ${fmtInt(cellPassed)} passed (${fmtPct(cellN ? num(cellPassed) / cellN : null)})`
                  : ""}
                {capped ? ` — list capped at ${LIGHTBOX_LIMIT}` : ""}
              </span>
              <span className="spacer" />
              <Toggle checked={failsOnly} onChange={setFailsOnly} label={`fails only (${failed})`} />
            </div>

            {visible.length === 0 ? (
              <Empty
                title={failsOnly && rows.length ? "No failing turns in this cell" : "No turns in this cell"}
                body={
                  failsOnly && rows.length
                    ? `All ${rows.length} turns behind this cell passed.`
                    : "Nothing matched the axis values this cell was aggregated under."
                }
              />
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Case</th>
                      <th className="num">Turn</th>
                      <th>Lane</th>
                      <th>User said</th>
                      <th>Result</th>
                      <th>Judge</th>
                      <th className="num">Latency</th>
                      <th className="num">Tokens</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((row) => (
                      <tr
                        key={`${row.conversation_id}#${row.turn_index}`}
                        style={{ cursor: "pointer" }}
                        onClick={() => setSelected(row)}
                      >
                        <td>{dash(row.case_id)}</td>
                        <td className="num">{row.turn_index}</td>
                        <td>{dash(row.turn_owner)}</td>
                        <td className="truncate" style={{ maxWidth: 280 }} title={row.user_text ?? ""}>
                          {dash(row.user_text)}
                        </td>
                        <td style={{ color: row.passed ? "var(--success)" : "var(--critical)" }}>
                          {row.passed ? "pass" : (row.fails ?? []).join(", ") || "fail"}
                        </td>
                        <td>{dash(row.judge_verdict)}</td>
                        <td className="num">{fmtMs(row.latency_ms)}</td>
                        <td className="num">{fmtInt(row.total_tokens)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Loadable>

      <DialogFooter>
        <span className="muted" style={{ marginRight: "auto", fontSize: 11 }}>
          {Object.entries(filters)
            .map(([k, v]) => `${k}=${v || "(blank)"}`)
            .join(" · ") || "no filters"}
        </span>
        <button className="btn" onClick={turns.reload}>
          Reload
        </button>
        <button className="btn" onClick={onClose}>
          Close
        </button>
      </DialogFooter>
    </Dialog>
  );
}
